import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { requireUserId } from './userSession';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  InputLabel,
  FormControl,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import * as XLSX from 'xlsx';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';

export default function PaymentLedgerReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [mode, setMode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  // CR/DR transaction-type filter (#9) - '' = All
  const [txnType, setTxnType] = useState('');

  const token = sessionStorage.getItem('token');

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'in', 'out'
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    bookingId: '',
    amount: '',
    mode: '',
    cardNumber: '',
    cardType: '',
    refNo: '',
    remark: '',
  });

  const fetchReport = async (selectedMode, fDate, tDate) => {
    setLoading(true);
    try {
      const modeParam = selectedMode || 'null';
      const fromDateParam = fDate || 'null';
      const toDateParam = tDate || 'null';

      const url = `${config.API_BASE_URL}/home/Getpaymentreports/${modeParam}/${fromDateParam}/${toDateParam}/${company?.id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      setReport(data);
      console.log('Payments Report:', data);
    } catch (err) {
      console.error('Error fetching payment report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(null, null, null);
  }, []);

  const handleFilterApply = () => {
    fetchReport(mode, fromDate, toDate);
  };

  const handleReset = () => {
    setMode('');
    setFromDate('');
    setToDate('');
    setSearchText('');
    setTxnType('');
    fetchReport(null, null, null);
  };

  const handleViewInvoice = (entry) => {
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/#/invoice/${entry.bookingId}`, '_blank');
  };

  // Modal functions (Add In / Add Out / Update)
  const openModal = (type) => {
    setModalType(type);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      bookingId: '',
      amount: '',
      mode: '',
      cardNumber: '',
      cardType: '',
      refNo: '',
      remark: '',
    });
    setModalOpen(true);
  };

  const openUpdateModal = (entry) => {
    setModalType(entry.type === 'CR' ? 'in' : 'out');
    setIsEditMode(true);
    setEditingId(entry.id);

    setFormData({
      bookingId: entry.bookingId?.toString() || '',
      amount: entry.amount?.toString() || '',
      mode: (entry.mode || '').toLowerCase(),
      cardNumber: entry.cardnumber || '',
      cardType: (entry.cardtype || '').toLowerCase(),
      refNo: entry.refno || '',
      remark: entry.remark || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitPayment = async () => {
    const type = modalType === 'in' ? 'CR' : 'DR';

    // Never write a ledger row with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    const payload = {
      id: isEditMode ? editingId : 0,
      bookingId: formData.bookingId || 0,
      amount: parseFloat(formData.amount) || 0,
      paymentType: modalType === 'in' ? 'IN' : 'OUT',
      mode: formData.mode,
      type,
      remark: formData.remark || null,
      refNo: formData.refNo || null,
      cardType: formData.cardType || null,
      cardNumber: formData.cardNumber || null,
      Companyid: company?.id,
      Userid: uid,
    };

    try {
      const url = `${config.API_BASE_URL}/home/Addledgerpayment`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        closeModal();
        fetchReport(mode, fromDate, toDate); // Refresh after add/update
      } else {
        toast.error('Failed to save payment');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Something went wrong');
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!report) {
    return (
      <Typography variant='h6' align='center' sx={{ mt: 4 }}>
        No data available
      </Typography>
    );
  }

  // Summary Cards
  const summaryCards = [
    {
      label: 'Total Due',
      value: report.totalDue || 0,
      color: '#d32f2f',
      bg: '#ffebee',
    },
    {
      label: 'Total UPI',
      value: report.totalUPI || 0,
      color: '#1976d2',
      bg: '#e3f2fd',
    },
    {
      label: 'Total Card',
      value: report.totalCard || 0,
      color: '#388e3c',
      bg: '#e8f5e9',
    },
    {
      label: 'Total Online',
      value: report.totalOnline || 0,
      color: '#f57c00',
      bg: '#fff3e0',
    },
  ];

  const filteredLedgers =
    report.ledgers?.filter(
      (entry) =>
        (!txnType ||
          (entry.type || '').toString().toUpperCase() === txnType) &&
        Object.values(entry).some((val) =>
          val?.toString().toLowerCase().includes(searchText.toLowerCase()),
        ),
    ) || [];

  // Export to Excel (basic implementation - you can enhance)
  const exportToExcel = () => {
    if (!report?.ledgers?.length) return toast.info('No data to export');

    const worksheet = XLSX.utils.json_to_sheet(filteredLedgers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
    XLSX.writeFile(
      workbook,
      `Payment_Ledger_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant='h4'
        fontWeight='bold'
        mb={3}
        textAlign='center'
        sx={{ color: '#0a2540' }}>
        💳 Payment Ledger Summary
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        {summaryCards.map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  textAlign: 'center',
                  background: item.bg,
                }}>
                <Typography variant='body2' color='text.secondary'>
                  {item.label}
                </Typography>
                <Typography
                  variant='h5'
                  fontWeight='bold'
                  sx={{ color: item.color, mt: 1 }}>
                  {Companycurrency} {item.value.toFixed(2)}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Add In / Add Out Buttons */}
      <Box display='flex' gap={2} mb={3}>
        <Button
          variant='contained'
          color='success'
          onClick={() => openModal('in')}>
          + Add In (CR)
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => openModal('out')}>
          - Add Out (DR)
        </Button>
      </Box>

      {/* Filters */}
      <Box display='flex' gap={2} mb={3} flexWrap='wrap' alignItems='center'>
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel>Mode</InputLabel>
          <Select
            value={mode}
            label='Mode'
            onChange={(e) => setMode(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='cash'>Cash</MenuItem>
            <MenuItem value='card'>Card</MenuItem>
            <MenuItem value='upi'>UPI</MenuItem>
            <MenuItem value='online'>Online</MenuItem>
            <MenuItem value='Book'>Booking (DR)</MenuItem>
            <MenuItem value='discount'>Discount</MenuItem>
          </Select>
        </FormControl>

        <FormControl size='small' sx={{ minWidth: 130 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={txnType}
            label='Type'
            onChange={(e) => setTxnType(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='CR'>CR</MenuItem>
            <MenuItem value='DR'>DR</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label='From Date'
          type='date'
          size='small'
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label='To Date'
          type='date'
          size='small'
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label='🔍 Search (any field)'
          size='small'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Button variant='contained' onClick={handleFilterApply}>
          Apply Filter
        </Button>

        <Button variant='outlined' onClick={handleReset}>
          Reset
        </Button>

        <Button
          variant='contained'
          color='secondary'
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}>
          Export Excel
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  '#',
                  'Usernmae',
                  'Ref No',
                  'Booking ID',
                  'Date',
                  'Mode',
                  'Type',
                  `Amount (${Companycurrency})`,
                  'Remark',
                  'Ref No',
                  'Card Type',
                  'Card Number',
                  'Actions',
                ].map((head, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      backgroundColor: '#0a2540',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLedgers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align='center'>
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLedgers.map((entry, i) => (
                  <TableRow key={entry.id || i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{entry.username}</TableCell>
                    <TableCell>Ref_{entry.id}</TableCell>
                    <TableCell
                      onClick={() => handleViewInvoice(entry)}
                      sx={{
                        cursor: 'pointer',
                        color: '#1976d2',
                        fontWeight: 'bold',
                      }}>
                      {entry.bookingId || '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(entry.tranjectiondate).toLocaleString('en-GB')}
                    </TableCell>
                    <TableCell>{entry.mode}</TableCell>
                    <TableCell>{entry.type}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {Companycurrency}{' '}
                      {parseFloat(entry.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{entry.remark || '-'}</TableCell>
                    <TableCell>{entry.refno || '-'}</TableCell>
                    <TableCell>{entry.cardtype || '-'}</TableCell>
                    <TableCell>{entry.cardnumber || '-'}</TableCell>

                    <TableCell>
                      <IconButton
                        color='primary'
                        onClick={() => openUpdateModal(entry)}
                        title='Edit Entry'>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Update Modal */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          {isEditMode ? 'Update' : 'Add'} {modalType === 'in' ? 'In' : 'Out'}{' '}
          Payment
        </DialogTitle>
        <DialogContent>
          <TextField
            label='Booking ID'
            name='bookingId'
            value={formData.bookingId}
            onChange={handleFormChange}
            fullWidth
            margin='dense'
          />
          <TextField
            label='Amount'
            name='amount'
            type='number'
            value={formData.amount}
            onChange={handleFormChange}
            fullWidth
            margin='dense'
          />

          <FormControl fullWidth margin='dense'>
            <InputLabel>Mode</InputLabel>
            <Select
              name='mode'
              value={formData.mode}
              onChange={handleFormChange}>
              <MenuItem value='cash'>Cash</MenuItem>
              <MenuItem value='card'>Card</MenuItem>
              <MenuItem value='upi'>UPI</MenuItem>
              <MenuItem value='online'>Online</MenuItem>
              <MenuItem value='Book'>Book</MenuItem>
              <MenuItem value='discount'>Discount</MenuItem>
            </Select>
          </FormControl>

          {formData.mode === 'card' && (
            <>
              <TextField
                label='Card Number'
                name='cardNumber'
                value={formData.cardNumber}
                onChange={handleFormChange}
                fullWidth
                margin='dense'
              />
              <FormControl fullWidth margin='dense'>
                <InputLabel>Card Type</InputLabel>
                <Select
                  name='cardType'
                  value={formData.cardType}
                  onChange={handleFormChange}>
                  <MenuItem value='debit'>Debit</MenuItem>
                  <MenuItem value='credit'>Credit</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {(formData.mode === 'upi' || formData.mode === 'online') && (
            <TextField
              label='Ref No'
              name='refNo'
              value={formData.refNo}
              onChange={handleFormChange}
              fullWidth
              margin='dense'
            />
          )}

          <TextField
            label='Remark'
            name='remark'
            value={formData.remark}
            onChange={handleFormChange}
            fullWidth
            margin='dense'
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            onClick={handleSubmitPayment}
            variant='contained'
            color={isEditMode ? 'primary' : 'success'}>
            {isEditMode ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
